import { Injectable, inject } from '@angular/core';
import {
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  map,
  takeUntil,
  tap,
} from 'rxjs';
import { ProductViewsActions } from 'src/app/entities/products/actions/product-views.actions';
import {
  BackendViewNames,
  ProductViews,
} from 'src/app/entities/products/enums/product-views.enum';
import {
  areArrayValuesEqual,
  makeObjectWithNoNullValues,
} from '@app-shared/functions/helpers.functions';
import { environment } from 'src/environments/environment';
import { SessionRepository } from '@app-core/state/session.repository';
import { AtcsRepository } from 'src/app/entities/filters/atc/state/atc.repository';
import { HttpClient } from '@angular/common/http';
import { emitOnce } from '@ngneat/elf';
import { UserStatus } from '@app-shared/enums/user-statuses.enum';
import { EXPECTED_STOCK_OUT_COUNTRIES } from '@app-shared/enums/country-codes.enum';

import { ProductsService } from '../../products/state/products.service';
import {
  IFavoriteFilter,
  IFavoriteFilterMeta,
  createFavoriteFilter,
} from './favorite-filter.model';
import { ManufacturerRepository } from '../../filters/manufacturers/state/manufacturer.repository';
import { OtherFiltersRepository } from '../../filters/other-filters/state/other-filters.repository';
import { DataFieldsRepository } from '../../filters/data-fields/state/data-fields.repository';
import { MoleculesRepository } from '../../filters/molecules/state/molecules.repository';
import { CountryRepository } from '../../countries/state/country.repository';
import { FavoriteFiltersRepository } from './favorite-filters.repository';
import { ProductsRepository } from '../../products/state/products.repository';
import { CountriesFilterRepository } from '../../filters/countries/state/countries-filter.repository';
import { ManufacturersService } from '../../filters/manufacturers/state/manufacturers.service';
import { IManufacturer } from '../../filters/manufacturers/state/manufacturer.model';
import { IMolecule } from '../../filters/molecules/state/molecule.model';
import { IATC } from '../../filters/atc/state/atc.model';

@Injectable({ providedIn: 'root' })
export class FavoriteFiltersService {
  private readonly countryRepo = inject(CountryRepository);
  private readonly sessionRepo = inject(SessionRepository);
  private readonly productsService = inject(ProductsService);
  private readonly productsRepo = inject(ProductsRepository);
  private readonly countriesFilterRepo = inject(CountriesFilterRepository);

  private readonly atcsRepo = inject(AtcsRepository);
  private readonly manufacturersRepo = inject(ManufacturerRepository);
  private readonly manufacturersService = inject(ManufacturersService);

  private readonly moleculesRepo = inject(MoleculesRepository);
  private readonly dataFieldsRepo = inject(DataFieldsRepository);
  private readonly otherFiltersRepo = inject(OtherFiltersRepository);
  private readonly ffRepo = inject(FavoriteFiltersRepository);
  private readonly http = inject(HttpClient);

  /** Cancels requests to the api */
  private cancelRequest = new Subject<void>();

  readonly hasAnyActiveFilters$ = combineLatest([
    this.productsRepo.searchTerm$,
    this.atcsRepo.selectedAtcs$,
    this.countriesFilterRepo.selectedCountries$,
    this.manufacturersRepo.selectedManufacturers$,
    this.moleculesRepo.selectedMolecules$,
    this.dataFieldsRepo.otc$,
    this.dataFieldsRepo.btm$,
    this.dataFieldsRepo.tender$,
    this.otherFiltersRepo.expectedShortageDate$,
    this.otherFiltersRepo.manufacturerTypeEnabled$,
    this.otherFiltersRepo.manufacturerType$,
  ]).pipe(
    debounceTime(0), // Wait for all synchronous updates to complete
    tap(() => this.checkForValidityOfActiveFilter()), // check if active filter is valid on filters change
    map(
      ([
        searchTerm,
        atcs,
        countries,
        manufacturers,
        molecules,
        otc,
        btm,
        tender,
        expectedShortageDate,
        manufacturerTypeEnabled,
        manufacturerType,
      ]) => {
        if (
          searchTerm ||
          atcs?.length ||
          countries?.length ||
          manufacturers?.length ||
          molecules?.length ||
          otc ||
          btm ||
          tender ||
          expectedShortageDate ||
          (manufacturerTypeEnabled ? !!manufacturerType : false) // only consider `manufacturerType` as active if `manufacturerTypeEnabled` is active
        ) {
          return true;
        } else {
          this.ffRepo.setActiveId(null); // clear the active filter when all filters are removed

          return false;
        }
      },
    ),
  );

  init(): void {
    this.sessionRepo.isLoggedIn$.subscribe(isLoggedIn => {
      if (isLoggedIn) {
        // eslint-disable-next-line rxjs/no-nested-subscribe
        this.getFavoriteFilters().subscribe({
          next: res => {
            this.setDefaultFavoriteFromList(res);

            const favFilter = this.ffRepo.getStoreProp('defaultFavoriteFilter');
            if (favFilter) this.applyFilter(favFilter);
          },
        });
      } else {
        this.cancelRequest.next();
        this.ffRepo.resetStore();
      }
    });
  }

  private checkForValidityOfActiveFilter(): void {
    this.ffRepo.isActiveFavoriteValid$.next(
      this.compareActiveFavoriteFilterWithOtherActiveFilters(),
    );
  }

  private setDefaultFavoriteFromList(ffList: IFavoriteFilter[]): void {
    try {
      const defaultFF = ffList.find(fav => fav.default);
      this.ffRepo.setDefaultFavoriteFilter(defaultFF!);
    } catch {
      this.ffRepo.setDefaultFavoriteFilter(null);
    }
  }

  /**
   * Gets a list of all saved favorite filters.
   *
   * GET /product/favorite
   */
  getFavoriteFilters(countryId?: number): Observable<IFavoriteFilter[]> {
    this.ffRepo.setLoading(true);

    const activeCountryId =
      countryId ?? this.countryRepo.getActiveCountry()?.id ?? null;
    const baseEndpoint = `${environment.apiUrl}/product/favorite`;
    const endpoint =
      activeCountryId !== null
        ? `${baseEndpoint}?country=${activeCountryId}&report=false`
        : `${baseEndpoint}?report=false`;

    return this.http.get<IFavoriteFilter[]>(endpoint).pipe(
      tap(res => {
        this.ffRepo.setFavoriteFilters(res);
        this.ffRepo.setLoading(false);
      }),
      takeUntil(this.cancelRequest),
    );
  }

  /**
   * Gets a list of report specific favorite filters for a given country.
   *
   * GET /product/favorite?country={countryId}&report=true
   *
   * @param countryId optional explicit country ID; falls back to the active country from the store
   */
  getReports(countryId?: number): Observable<IFavoriteFilter[]> {
    this.ffRepo.setLoading(true);

    const activeCountryId =
      countryId ?? this.countryRepo.getActiveCountry()?.id ?? null;
    const baseEndpoint = `${environment.apiUrl}/product/favorite`;
    const endpoint =
      activeCountryId !== null
        ? `${baseEndpoint}?country=${activeCountryId}&report=true`
        : `${baseEndpoint}?report=true`;

    return this.http.get<IFavoriteFilter[]>(endpoint).pipe(
      tap(res => {
        this.ffRepo.setReportFavoriteFilters(res);
        this.ffRepo.setLoading(false);
      }),
      takeUntil(this.cancelRequest),
    );
  }

  /**
   * Create a new favorite filter.
   *
   * POST /product/favorite
   *
   * @param name
   * @param appNotification
   * @param emailNotification
   * @param shortageNotifications
   * @param backInStockNotifications
   *
   * @returns the created favorite filter
   */
  saveFavoriteFilter(
    name: string,
    appNotification: boolean,
    emailNotification: boolean,
    shortageNotifications: boolean,
    backInStockNotifications: boolean,
    setActive = true,
  ): Observable<IFavoriteFilter> {
    const favoriteFilter = makeObjectWithNoNullValues(
      this.createFavoriteWithActiveFilters({
        name,
        application_notifications: appNotification,
        email_notifications: emailNotification,
        shortage_notifications: shortageNotifications,
        back_in_stock_notifications: backInStockNotifications,
      }),
    );

    return this.http
      .post<IFavoriteFilter>(
        environment.apiUrl + `/product/favorite`,
        favoriteFilter,
      )
      .pipe(
        tap(filter => {
          emitOnce(() => {
            // Update store with new filter
            const currentFilters = this.ffRepo.getFavoriteFilters();
            this.ffRepo.setFavoriteFilters([
              ...currentFilters,
              createFavoriteFilter(filter),
            ]);

            if (setActive) {
              this.ffRepo.setActiveId(filter.id!);
            }
          });
        }),
      );
  }

  /**
   * Edit a favorite filter.
   *
   * PUT /product/favorite/{favoriteSearch}
   *
   * @param favoriteFilter
   * @returns the edited favorite filter
   */
  editFavoriteFilter(ff: IFavoriteFilter): Observable<IFavoriteFilter> {
    // the reason this is done is because we need to send null value for
    // otc, btm and tender if they are set to false, per BE request
    const favFilter: IFavoriteFilter = this.createFavoriteWithActiveFilters({
      ...ff,
      filters: {
        ...ff.filters,
        otc: ff.filters.otc || null,
        btm: ff.filters.btm || null,
        tender: ff.filters.tender || null,
      },
    });

    return this.http
      .put<IFavoriteFilter>(
        environment.apiUrl + `/product/favorite/${ff.id}`,
        favFilter,
      )
      .pipe(
        tap(filter => {
          emitOnce(() => {
            const updatedFilter = createFavoriteFilter(filter);
            const currentFilters = this.ffRepo.getFavoriteFilters();
            const updatedFilters = currentFilters.map(f => {
              return f.id === filter.id ? updatedFilter : f;
            });
            this.ffRepo.setFavoriteFilters(updatedFilters);
            const currentReportFilters = this.ffRepo.getReportFavoriteFilters();
            const updatedReportFilters = currentReportFilters.map(f => {
              return f.id === filter.id ? updatedFilter : f;
            });
            this.ffRepo.setReportFavoriteFilters(updatedReportFilters);
            this.checkForValidityOfActiveFilter();
          });
        }),
      );
  }

  /**
   * Remove the saved favorite filter.
   *
   * DELETE /product/favorite/{id}
   *
   * @param id filter ID
   */
  removeFavoriteFilterByID(id: number): Observable<void> {
    this.ffRepo.setLoading(true);

    return this.http
      .delete<void>(environment.apiUrl + `/product/favorite/${id}`)
      .pipe(
        tap(() => {
          this.ffRepo.remove(id);
          this.ffRepo.removeReport(id);
          this.checkForValidityOfActiveFilter();
          this.ffRepo.setLoading(false);
        }),
      );
  }

  /**
   * Compares the active favorite filter with other filters from services to check if the one currently active matches
   * other active filters.
   *
   * @returns true if active favorite has the same values as other filter's values
   */
  private compareActiveFavoriteFilterWithOtherActiveFilters(): boolean {
    const activeFavoriteFilter: IFavoriteFilter | null =
      this.ffRepo.getActiveFilter();
    if (!activeFavoriteFilter) return false;

    let queryEqual = false;

    if (
      !!activeFavoriteFilter.query &&
      !!this.productsRepo.getStoreProp('searchTerm')
    ) {
      // if both are truthy, compare values
      queryEqual =
        activeFavoriteFilter.query ===
        this.productsRepo.getStoreProp('searchTerm');
    } else if (
      !!activeFavoriteFilter.query !==
      !!this.productsRepo.getStoreProp('searchTerm')
    ) {
      // else if only one is falsy, means they are not the same
      queryEqual = false;
    } // otherwise the are equal
    else {
      queryEqual = true;
    }

    if (
      queryEqual &&
      areArrayValuesEqual(
        activeFavoriteFilter.filters?.atc.map(a => a.id),
        this.atcsRepo.getStoreProp('selectedAtcsIDs'),
      ) &&
      areArrayValuesEqual(
        activeFavoriteFilter.filters?.manufacturer.map(a => a.id),
        this.manufacturersRepo.getSelectedManufacturersIds(),
      ) &&
      areArrayValuesEqual(
        activeFavoriteFilter.filters?.molecule.map(a => a.id),
        this.moleculesRepo.getSelectedMoleculesIds(),
      ) &&
      activeFavoriteFilter.filters.otc === this.dataFieldsRepo.getState().otc &&
      activeFavoriteFilter.filters.btm === this.dataFieldsRepo.getState().btm &&
      activeFavoriteFilter.filters.tender ===
        this.dataFieldsRepo.getState().tender &&
      activeFavoriteFilter.filters.expected_shortage_date ===
        this.otherFiltersRepo.getStoreProp('expected_shortage_date') &&
      activeFavoriteFilter.filters.manufacturer_type ===
        this.otherFiltersRepo.getStoreProp('manufacturer_type')
    ) {
      return true;
    } else return false;
  }

  /**
   * Creates a favorite with currently active filters.
   *
   * @param params
   */
  createFavoriteWithActiveFilters(
    params: Partial<IFavoriteFilter>,
  ): IFavoriteFilter {
    // Transform region manufacturers, molecules and ATCs data  - START
    // region manufacturers ID value is array of numbers, for each ID we have to push separate IManufacturer object with same name because that is backend required format, we can't send array of IDs
    const selectedManufacturers =
      this.manufacturersRepo.getStoreProp('selectedManufacturers') ?? [];

    // REPORTS - this is to check if we are in report mode
    // and if so, we need to use the manufacturers from the params
    const manufacturersSource =
      (selectedManufacturers.length
        ? selectedManufacturers
        : params.filters?.manufacturer) ?? [];

    const transformedManufacturers: IManufacturer[] = [];

    manufacturersSource.forEach(manufacturer => {
      if (typeof manufacturer.id === 'number') {
        transformedManufacturers.push(manufacturer);
      } else {
        manufacturer.id.forEach(id => {
          transformedManufacturers.push({ ...manufacturer, id });
        });
      }
    });

    // region molecules ID value is array of numbers, for each ID we have to push separate IMolecule object with same name because that is backend required format, we can't send array of IDs
    const selectedMolecules =
      this.moleculesRepo.getStoreProp('selectedMolecules') ?? [];
    // REPORTS - this is to check if we are in report mode
    // and if so, we need to use the molecules from the params
    const moleculesSource =
      (selectedMolecules.length
        ? selectedMolecules
        : params.filters?.molecule) ?? [];

    const transformedMolecules: IMolecule[] = [];

    moleculesSource.forEach(molecule => {
      if (typeof molecule.id === 'number') {
        transformedMolecules.push(molecule);
      } else {
        molecule.id.forEach(id => {
          transformedMolecules.push({ ...molecule, id });
        });
      }
    });

    // region ATCs ID value is array of numbers, for each ID we have to push separate IATC object with same name because that is backend required format, we can't send array of IDs
    const selectedATCs = this.atcsRepo.getStoreProp('selectedAtcs') ?? [];
    // REPORTS - this is to check if we are in report mode
    // and if so, we need to use the ATCs from the params
    const atcsSource =
      (selectedATCs.length ? selectedATCs : params.filters?.atc) ?? [];

    const transformedATCs: IATC[] = [];

    atcsSource.forEach(atc => {
      if (typeof atc.id === 'number') {
        transformedATCs.push(atc);
      } else {
        atc.id.forEach(id => {
          transformedATCs.push({ ...atc, id });
        });
      }
    });
    // Transform region manufacturers, molecules and ATCs data  - END

    return createFavoriteFilter({
      ...params,

      query: this.productsRepo.getStoreProp('searchTerm') || '',
      filters: {
        atc: transformedATCs || [],
        countries: this.countriesFilterRepo.getSelectedCountries() || [],
        manufacturer: transformedManufacturers || [],
        molecule: transformedMolecules || [],
        otc: this.dataFieldsRepo.getState().otc || null, // we need to send null here if the value is false as per BE request
        btm: this.dataFieldsRepo.getState().btm || null, // we need to send null here if the value is false as per BE request
        tender: this.dataFieldsRepo.getState().tender || null, // we need to send null here if the value is false as per BE request
        expected_shortage_date:
          this.otherFiltersRepo.getState().expected_shortage_date,
        manufacturer_type: this.otherFiltersRepo.getStoreProp(
          'manufacturer_type_enabled',
        )
          ? this.otherFiltersRepo.getStoreProp('manufacturer_type')
          : null,
      },

      user_id: this.sessionRepo.getStoreProp('authUser')?.id,
    });
  }

  /**
   * Checks if the current user has access to Expected Stock Out tab.
   * User must be VIP and the active country must be in EXPECTED_STOCK_OUT_COUNTRIES.
   *
   * @returns true if user can access Expected Stock Out
   */
  private canAccessExpectedStockOut(): boolean {
    const authUser = this.sessionRepo.getStoreProp('authUser');
    const activeCountry = this.countryRepo.getActiveCountry();

    // Check if user is VIP
    const isVIP = authUser?.statuses?.includes(UserStatus.VIP) || false;

    // Check if active country is in allowed countries
    const isCountryAllowed = activeCountry
      ? EXPECTED_STOCK_OUT_COUNTRIES.includes(activeCountry.country_code)
      : false;

    return isVIP && isCountryAllowed;
  }

  /**
   * Sets the filter as active in the store and sets all other filters in their relative stores/services.
   *
   * @param favFilter
   */
  applyFilter(favFilter: IFavoriteFilter): void {
    this.productsService.resetStore();
    this.atcsRepo.setSelectedAtcs([]); // we must not reset the entire store here since the ATC list is always the same, only the selection changes
    this.manufacturersRepo.resetStore();
    this.moleculesRepo.resetStore();
    this.dataFieldsRepo.resetStore();

    if (favFilter.target_meta) {
      // if we have meta data, we need to set the appropriate default filters
      switch (favFilter.target_meta.tab) {
        case BackendViewNames.SHORTAGE:
          ProductViewsActions.VIEW_SHORTAGE.setDefaultFilters(
            this.productsRepo,
            this.countryRepo.getActiveCountry()!,
          );
          this.productsRepo.setProductView(ProductViews.SHORTAGE);
          break;

        case BackendViewNames.BACK_IN_STOCK:
          ProductViewsActions.VIEW_BACK_IN_STOCK.setDefaultFilters(
            this.productsRepo,
            this.countryRepo.getActiveCountry()!,
          );
          this.productsRepo.setProductView(ProductViews.BACK_IN_STOCK);
          break;

        case BackendViewNames.NOVELTIES:
          ProductViewsActions.VIEW_NOVELTIES.setDefaultFilters(
            this.productsRepo,
            this.countryRepo.getActiveCountry()!,
            this.sessionRepo,
          );
          this.productsRepo.setProductView(ProductViews.NOVELTIES);
          break;

        case BackendViewNames.SORTALYS:
          ProductViewsActions.VIEW_SORTALYS.setDefaultFilters(
            this.productsRepo,
            this.countryRepo.getActiveCountry()!,
            this.sessionRepo,
          );
          this.productsRepo.setProductView(ProductViews.SORTALYS);
          break;

        case BackendViewNames.ORDERS:
          ProductViewsActions.VIEW_ORDERS.setDefaultFilters(
            this.productsRepo,
            this.countryRepo.getActiveCountry()!,
          );
          this.productsRepo.setProductView(ProductViews.ORDERS);
          break;

        case BackendViewNames.EXPECTED_STOCK_OUT:
          // Check if user has VIP access and is in allowed country
          if (this.canAccessExpectedStockOut()) {
            ProductViewsActions.EXPECTED_STOCK_OUT.setDefaultFilters(
              this.productsRepo,
              this.countryRepo.getActiveCountry()!,
            );
            this.productsRepo.setProductView(ProductViews.EXPECTED_STOCK_OUT);
          } else {
            // Fallback to ALL view if user doesn't have access
            ProductViewsActions.VIEW_ALL.setDefaultFilters(
              this.productsRepo,
              this.countryRepo.getActiveCountry()!,
            );
            this.productsRepo.setProductView(ProductViews.ALL);
          }
          break;

        default:
          ProductViewsActions.VIEW_ALL.setDefaultFilters(
            this.productsRepo,
            this.countryRepo.getActiveCountry()!,
          );
          this.productsRepo.setProductView(ProductViews.ALL);
          break;
      }
    } else {
      ProductViewsActions.VIEW_ALL.setDefaultFilters(
        this.productsRepo,
        this.countryRepo.getActiveCountry()!,
      );
    }

    emitOnce(() => {
      this.productsRepo.updateStoreProps({
        searchTerm: favFilter.query || '',
        currentPage: 1,
        selectedProduct: null,
      });
      this.productsRepo.setStoreProp(
        'suggestionsSearchTerm',
        favFilter.query || '',
      );

      this.atcsRepo.setSelectedAtcs(favFilter.filters?.atc || []);

      this.countriesFilterRepo.setSelectedCountries(
        favFilter.filters?.countries || [],
      );

      this.manufacturersService.setSelectedManufacturers(
        favFilter.filters?.manufacturer || [],
      );
      this.moleculesRepo.setSelectedMolecules(
        favFilter.filters?.molecule || [],
      );

      this.dataFieldsRepo.setOTC(favFilter.filters.otc);
      this.dataFieldsRepo.setBTM(favFilter.filters.btm);
      this.dataFieldsRepo.setTender(favFilter.filters.tender);

      this.otherFiltersRepo.setExpectedShortageDate(
        !!favFilter.filters.expected_shortage_date,
      );
      this.otherFiltersRepo.setManufacturerTypeEnabled(
        !!favFilter.filters.manufacturer_type,
      );
      this.otherFiltersRepo.setManufacturerType(
        favFilter.filters.manufacturer_type,
      );

      this.setActiveFilter(favFilter);
    });
  }

  /**
   * Only sets the filter as active in this store.
   *
   * @param ff
   */
  setActiveFilter(ff: IFavoriteFilter): void {
    this.ffRepo.setActiveId(ff?.id || null);
  }

  setFavoriteFilterByMeta(meta: IFavoriteFilterMeta): void {
    const filters = this.ffRepo.getFavoriteFilters();
    const filter = filters.find(
      f =>
        f.target_meta?.parent === meta.parent &&
        f.target_meta?.target === meta.target &&
        f.target_meta?.tab === meta.tab,
    );

    if (filter) {
      this.applyFilter(filter);
    }
  }
}
